import { always, contains, not } from 'ramda';
import React, { createContext, FC, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { MediaType } from '../../constants/MediaType';
import { MediaManagerSDKFile } from '../media-manager-sdk';
import { canItemBeBought } from '../media-manager-sdk/items';
import { useAdapter } from './useAdapter';
import { MainSource } from '../../constants/Source';
import { useQuery } from 'react-query';
import { captureException } from '@sentry/react';

interface IOuter {
  price?: string;
}

interface DigitalGood {
  priceValue: string;
  priceWithTax: string;
  currencyCode: string;
  currencySymbol: string;
  showPricesWithTax: boolean;
  taxType: string;
  displayTaxType: string;
}

interface DigitalGoodsResponce {
  [key: string]: DigitalGood;
}

const GetItemPriceContext = createContext({ getTaxSettings: (_item) => null, getItemPrice: (_item) => null });

const useExternalPrices = () => {
  const { formatNumber } = useIntl();
  const shutterstockAdapter = useAdapter(MainSource.Shutterstock);
  const {
    isLoading,
    data: prices,
    error,
  } = useQuery<DigitalGoodsResponce, Error>('prices', shutterstockAdapter.items.prices);

  if (isLoading) {
    return {};
  }

  if (error) {
    captureException(
      new Error(`Failed to get prices: ${error.message}`, {
        cause: error,
      }),
    );
    return {};
  }

  const imagePriceResponse = prices[MediaType.Picture];
  const videoPriceResponse = prices[MediaType.Video];
  return {
    [MediaType.Picture]: formatNumber(
      Number(imagePriceResponse.showPricesWithTax ? imagePriceResponse.priceWithTax : imagePriceResponse.priceValue),
      {
        style: 'currency',
        currency: imagePriceResponse.currencyCode,
      },
    ),
    [MediaType.Video]: formatNumber(
      Number(videoPriceResponse.showPricesWithTax ? videoPriceResponse.priceWithTax : videoPriceResponse.priceValue),
      {
        style: 'currency',
        currency: videoPriceResponse.currencyCode,
      },
    ),
    taxSettings: {
      showPricesWithTax: prices[MediaType.Picture].showPricesWithTax,
      displayTaxType: prices[MediaType.Picture].displayTaxType,
    },
  };
};

const StubGetItemPriceProvider: FC<IOuter> = ({ price, children }) => {
  const getItemPrice = useCallback(always(price), [price]);

  return (
    <GetItemPriceContext.Provider value={{ getTaxSettings: () => null, getItemPrice }}>
      {children}
    </GetItemPriceContext.Provider>
  );
};

const RealGetItemPriceProvider: FC = ({ children }) => {
  const priceRoot = useExternalPrices();

  const getTaxSettings = useCallback(
    (item: MediaManagerSDKFile) => {
      if (not(contains(item.mediaType, [MediaType.Picture, MediaType.Video])) || !canItemBeBought(item)) {
        return null;
      }

      return priceRoot?.taxSettings;
    },
    [priceRoot],
  );

  const getItemPrice = useCallback(
    (item: MediaManagerSDKFile) => {
      if (not(contains(item.mediaType, [MediaType.Picture, MediaType.Video])) || !canItemBeBought(item)) {
        return null;
      }

      return priceRoot?.[item.mediaType];
    },
    [priceRoot],
  );

  return (
    <GetItemPriceContext.Provider value={{ getTaxSettings, getItemPrice }}>{children}</GetItemPriceContext.Provider>
  );
};

export const GetItemPriceProvider: FC<IOuter> = (props) =>
  props.price ? <StubGetItemPriceProvider {...props} /> : <RealGetItemPriceProvider {...props} />;

export const useGetItemPrice = () => useContext(GetItemPriceContext);
